import { isDefined, isNonEmpty } from '@sgme/fp';
import type { OrderType } from 'apiConnector/blotters/secondaryOrderBlotter';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { FC, useCallback } from 'react';

import { BlotterPanelStatusField } from '../../../components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { formatToPercentOrAmount } from '../../../components/shared/Blotter/Grid/Hooks/usePercentOrAmountColumnType';
import { useSPIntl } from '../../../hooks/useIntl';
import { displayDateFormatOptions } from '../../../utils/date';
import type { SelectedItem } from '../Blotter/SecondaryOrderBlotter.connect';

export const SecondaryOrderSidePanelDetails: FC<{ item: SelectedItem }> = ({
  item: {
    dealId,
    orderId,
    orderType,
    isin,
    fileCode,
    price,
    priceType,
    requester,
    salesCounterpartName,
    orderStatus,
    currency,
    adjustedSalesMargin,
    nominal,
    settlementQuantity,
    settlementCashAmount,
    tradeDate,
    valuationDate,
    cobDate,
    settlementDate,
    tradingPrice,
    accruedInterest,
    adjustedTradingPrice,
    adjustedUpfrontFee,
    salesMargin,
    upfront,
    clientLimit,
  },
}) => {
  const { formatNumber, formatDate } = useSPIntl();
  const cashAmountOrPercent = useCallback(
    (val?: any) =>
      isDefined(val) && isNonEmpty(priceType)
        ? formatToPercentOrAmount(val, { priceType: priceType, currency: currency }, formatNumber)
        : undefined,
    [priceType, currency, formatNumber],
  );
  const fields = [
    {
      key: 'orderId' satisfies keyof SelectedItem,
      value: <span title={orderId}>{orderId}</span>,
    },
    {
      key: 'dealId' satisfies keyof SelectedItem,
      value: dealId,
    },
    {
      key: 'isin' satisfies keyof SelectedItem,
      value: isin,
    },
    {
      key: 'fileCode' satisfies keyof SelectedItem,
      value: fileCode,
    },
    {
      key: 'user',
      value: <span title={requester}>{requester}</span>,
    },
    {
      key: 'salesCounterpart',
      value: <span title={salesCounterpartName}>{salesCounterpartName}</span>,
    },
    {
      key: 'status',
      value: orderStatus !== undefined ? <BlotterPanelStatusField value={orderStatus} /> : undefined,
    },
    {
      key: 'currency' satisfies keyof SelectedItem,
      value: currency,
    },
    {
      key: 'nominal' satisfies keyof SelectedItem,
      value: nominal !== undefined ? formatNumber(nominal, { maximumFractionDigits: 2 }) : undefined,
    },
    {
      key: 'quantity',
      value:
        settlementQuantity !== undefined ? formatNumber(settlementQuantity, { maximumFractionDigits: 2 }) : undefined,
    },
    {
      key: 'cashAmount',
      value:
        settlementCashAmount !== undefined
          ? `${formatNumber(settlementCashAmount, { maximumFractionDigits: 2 })} ${currency}`
          : undefined,
    },
    {
      key: 'tradeDate' satisfies keyof SelectedItem,
      value: tradeDate !== undefined ? formatDate(tradeDate, displayDateFormatOptions) : undefined,
    },
    {
      key: 'valuationDate' satisfies keyof SelectedItem,
      value: valuationDate !== undefined ? formatDate(valuationDate, displayDateFormatOptions) : undefined,
    },
    {
      key: 'cobDate' satisfies keyof SelectedItem,
      value: cobDate !== undefined ? formatDate(cobDate, displayDateFormatOptions) : undefined,
    },
    {
      key: 'settlementDate' satisfies keyof SelectedItem,
      value: settlementDate !== undefined ? formatDate(settlementDate, displayDateFormatOptions) : undefined,
    },
    {
      key: 'accruedInterest' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(accruedInterest),
    },
    {
      key: 'price' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(price),
    },
    {
      key: 'tradingPrice' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(tradingPrice),
    },
    {
      key: 'salesMargin' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(salesMargin),
    },
    {
      key: 'clientLimit' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(clientLimit),
    },
    {
      key: 'upfront' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(upfront),
    },
    {
      key: 'adjustedUpfrontFee' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(adjustedUpfrontFee),
    },
    {
      key: 'adjustedSalesMargin' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(adjustedSalesMargin),
    },
    {
      key: 'adjustedTradingPrice' satisfies keyof SelectedItem,
      value: cashAmountOrPercent(adjustedTradingPrice),
    },
  ] as const;
  type FieldsKeys = (typeof fields)[number]['key'];
  const filterFieldsByType = () => {
    if (!orderType || orderType === 'Order on close') {
      return fields;
    }
    const fieldsToExcludeByType: Record<Exclude<OrderType, 'Order on close'>, FieldsKeys[]> = {
      Live: ['cobDate', 'valuationDate'],
      'Limit order': ['cobDate', 'adjustedTradingPrice', 'adjustedUpfrontFee', 'valuationDate'],
    };
    return fields.filter(x => !fieldsToExcludeByType[orderType].includes(x.key));
  };

  return (
    <ListGroup className="mt-3">
      {filterFieldsByType().map(item => (
        <ListGroupItem
          key={item.key}
          labelKey={`secondaryMarketOrderBlotter.details.${item.key}` as any}
          className="ps-0 pe-3"
        >
          {item.value ?? '_'}
        </ListGroupItem>
      ))}
    </ListGroup>
  );
};

SecondaryOrderSidePanelDetails.displayName = 'SecondaryOrderSidePanelDetails';
